import i18n from "@/utils/i18n";

/**
 * Permission dependency map.
 *
 * When a permission is checked in the role editor, all of its transitive
 * dependencies are auto-checked. Unchecking does NOT cascade — other checked
 * permissions may still need the dependency (safer to keep an extra VIEW
 * than to silently break another feature that shares the same lookup).
 *
 * General rule (applies to every module):
 *   X.CREATE / X.UPDATE / X.DELETE → X.VIEW
 *   Reason: the admin flows for these actions live on the list screen; the
 *   list endpoint is guarded by X.VIEW. Without it the page never loads so
 *   the user can't trigger create/update/delete anyway.
 *
 * Extra entries below call out WHY a rule beyond the general pattern exists
 * (form dropdowns, list columns, embedded widgets, etc.).
 *
 * Source: derived from a full sweep of `src/features/ * /services/*.api.ts`
 * endpoints vs backend `@Permission` decorators in `euas-backend`.
 * Master-data reads that are open to any authenticated user
 * (EDUCATION_CATEGORIES list, LOCATIONS list, instructors list) are NOT
 * listed here because the backend no longer guards them.
 */
export const PERMISSION_DEPENDENCIES: Record<string, string[]> = {
  // ────────────────────────────────────────────────────────────────
  // ANNOUNCEMENTS
  // VIEW → DEPARTMENTS.VIEW because the list/detail screens show
  // "Hedef Kitle" (birim) names, loaded from /departments.
  // CREATE/UPDATE also need it for the target-department dropdown
  // in the form.
  // ────────────────────────────────────────────────────────────────
  "ANNOUNCEMENTS.VIEW": ["DEPARTMENTS.VIEW"],
  "ANNOUNCEMENTS.CREATE": ["ANNOUNCEMENTS.VIEW", "DEPARTMENTS.VIEW"],
  "ANNOUNCEMENTS.UPDATE": ["ANNOUNCEMENTS.VIEW", "DEPARTMENTS.VIEW"],
  "ANNOUNCEMENTS.DELETE": ["ANNOUNCEMENTS.VIEW"],

  // Audit logs — standalone read-only module; no dependencies.
  "AUDIT_LOGS.VIEW": [],

  // Banned users — pure CRUD, only VIEW needed.
  "BANNED_USERS.CREATE": ["BANNED_USERS.VIEW"],
  "BANNED_USERS.UPDATE": ["BANNED_USERS.VIEW"],
  "BANNED_USERS.DELETE": ["BANNED_USERS.VIEW"],

  // Companies — pure CRUD.
  "COMPANIES.CREATE": ["COMPANIES.VIEW"],
  "COMPANIES.UPDATE": ["COMPANIES.VIEW"],
  "COMPANIES.DELETE": ["COMPANIES.VIEW"],

  // Departments — pure CRUD.
  "DEPARTMENTS.CREATE": ["DEPARTMENTS.VIEW"],
  "DEPARTMENTS.UPDATE": ["DEPARTMENTS.VIEW"],
  "DEPARTMENTS.DELETE": ["DEPARTMENTS.VIEW"],

  // Domain settings — single-row settings page; UPDATE uses the same
  // GET endpoint to prefill the form.
  "DOMAIN_SETTINGS.UPDATE": ["DOMAIN_SETTINGS.VIEW"],

  // ────────────────────────────────────────────────────────────────
  // EDUCATIONS
  // VIEW → EXAMS/SURVEYS because the education detail drawer shows
  // linked "Bağlı sınav" and "Bağlı anket" metadata (/exams, /surveys).
  // CREATE/UPDATE additionally need USERS.VIEW for the instructor
  // dropdown (/users/instructors) and the per-education manager picker
  // (/users search).
  // USERS.VIEW transitively pulls in its own 5 dependencies (see below),
  // so checking EDUCATIONS.CREATE auto-enables ~10 permissions.
  // ────────────────────────────────────────────────────────────────
  "EDUCATIONS.VIEW": ["EXAMS.VIEW", "SURVEYS.VIEW"],
  "EDUCATIONS.CREATE": [
    "EDUCATIONS.VIEW",
    "EXAMS.VIEW",
    "SURVEYS.VIEW",
    "USERS.VIEW",
  ],
  "EDUCATIONS.UPDATE": [
    "EDUCATIONS.VIEW",
    "EXAMS.VIEW",
    "SURVEYS.VIEW",
    "USERS.VIEW",
  ],
  "EDUCATIONS.DELETE": ["EDUCATIONS.VIEW"],

  // Education categories — pure CRUD.
  "EDUCATION_CATEGORIES.CREATE": ["EDUCATION_CATEGORIES.VIEW"],
  "EDUCATION_CATEGORIES.UPDATE": ["EDUCATION_CATEGORIES.VIEW"],
  "EDUCATION_CATEGORIES.DELETE": ["EDUCATION_CATEGORIES.VIEW"],

  // Email broadcast — SEND needs VIEW to load the history/template list.
  "EMAIL_BROADCAST.SEND": ["EMAIL_BROADCAST.VIEW"],

  // Exams — pure CRUD.
  "EXAMS.CREATE": ["EXAMS.VIEW"],
  "EXAMS.UPDATE": ["EXAMS.VIEW"],
  "EXAMS.DELETE": ["EXAMS.VIEW"],

  // Files — pure CRUD. (CREATE = upload, UPDATE = rename/metadata, etc.)
  "FILES.CREATE": ["FILES.VIEW"],
  "FILES.UPDATE": ["FILES.VIEW"],
  "FILES.DELETE": ["FILES.VIEW"],

  // ────────────────────────────────────────────────────────────────
  // INTERNS
  // VIEW → DEPARTMENTS.VIEW because the list has a birim column/filter.
  // CREATE/UPDATE forms use a user search (/users) + birim dropdown,
  // so USERS.VIEW and DEPARTMENTS.VIEW are required.
  // ────────────────────────────────────────────────────────────────
  "INTERNS.VIEW": ["DEPARTMENTS.VIEW"],
  "INTERNS.CREATE": ["INTERNS.VIEW", "DEPARTMENTS.VIEW", "USERS.VIEW"],
  "INTERNS.UPDATE": ["INTERNS.VIEW", "DEPARTMENTS.VIEW", "USERS.VIEW"],
  "INTERNS.DELETE": ["INTERNS.VIEW"],

  // Library — pure CRUD.
  "LIBRARY.CREATE": ["LIBRARY.VIEW"],
  "LIBRARY.UPDATE": ["LIBRARY.VIEW"],
  "LIBRARY.DELETE": ["LIBRARY.VIEW"],

  // Locations — pure CRUD.
  "LOCATIONS.CREATE": ["LOCATIONS.VIEW"],
  "LOCATIONS.UPDATE": ["LOCATIONS.VIEW"],
  "LOCATIONS.DELETE": ["LOCATIONS.VIEW"],

  // Personnel groups — pure CRUD.
  "PERSONNEL_GROUPS.CREATE": ["PERSONNEL_GROUPS.VIEW"],
  "PERSONNEL_GROUPS.UPDATE": ["PERSONNEL_GROUPS.VIEW"],
  "PERSONNEL_GROUPS.DELETE": ["PERSONNEL_GROUPS.VIEW"],

  // ────────────────────────────────────────────────────────────────
  // ROLES & PERMISSIONS
  // Rol düzenleme ekranı permission matrix çeker → PERMISSIONS.VIEW.
  // Yetki atama/iptal aksiyonları hedef kullanıcı araması yapar → USERS.VIEW.
  // ────────────────────────────────────────────────────────────────
  "ROLES.CREATE": ["ROLES.VIEW", "PERMISSIONS.VIEW"],
  "ROLES.UPDATE": ["ROLES.VIEW", "PERMISSIONS.VIEW"],
  "ROLES.DELETE": ["ROLES.VIEW"],
  "PERMISSIONS.ASSIGN": ["PERMISSIONS.VIEW", "USERS.VIEW"],
  "PERMISSIONS.REVOKE": ["PERMISSIONS.VIEW", "USERS.VIEW"],
  // Rol atama/kaldırma Personel sayfasından tetiklenir — kullanıcı arama +
  // rol listesi gerekir.
  "ROLES.ASSIGN": ["ROLES.VIEW", "USERS.VIEW"],
  "ROLES.REVOKE": ["ROLES.VIEW", "USERS.VIEW"],

  // ────────────────────────────────────────────────────────────────
  // REPORTS
  // Raporların filtre paneli eğitim ve personel seçimi yaptırır; sonuç
  // satırlarında her iki nesnenin de adı/alanları gösterilir. Her iki
  // bağımlılık kendi alt ağacını da getirir (toplam ~10 permission).
  // ────────────────────────────────────────────────────────────────
  "REPORTS.VIEW": ["EDUCATIONS.VIEW", "USERS.VIEW"],

  // SMS broadcast — SEND needs VIEW for history/template list.
  "SMS_BROADCAST.SEND": ["SMS_BROADCAST.VIEW"],

  // Support tickets — pure CRUD.
  "SUPPORT_TICKETS.CREATE": ["SUPPORT_TICKETS.VIEW"],
  "SUPPORT_TICKETS.UPDATE": ["SUPPORT_TICKETS.VIEW"],
  "SUPPORT_TICKETS.DELETE": ["SUPPORT_TICKETS.VIEW"],

  // Surveys — pure CRUD.
  "SURVEYS.CREATE": ["SURVEYS.VIEW"],
  "SURVEYS.UPDATE": ["SURVEYS.VIEW"],
  "SURVEYS.DELETE": ["SURVEYS.VIEW"],

  // ────────────────────────────────────────────────────────────────
  // USERS (Personel) — sistemin en geniş "hub" yetkisi.
  //
  // Personel liste + detay ekranında şu bileşenler var:
  //   • Birim kolonu/filtresi      → DEPARTMENTS.VIEW
  //   • Bağlı şirket adı           → COMPANIES.VIEW
  //   • Atanmış rol etiketleri     → ROLES.VIEW
  //   • "Girdiği sınavlar" sekmesi → EXAMS.VIEW
  //   • "Son işlemler" sekmesi     → AUDIT_LOGS.VIEW (kullanıcıya ait
  //                                  audit-log entry'leri)
  //
  // Oluşturma/güncelleme formunda da birim, şirket ve rol dropdown'ları
  // aynı veriyi kullanır; kayıt sonrası detay ekranı açıldığı için
  // AUDIT_LOGS de gerekir.
  //
  // USERS.VIEW kendisi başka bir yerde (EDUCATIONS.CREATE/UPDATE,
  // INTERNS.CREATE/UPDATE, PERMISSIONS.ASSIGN/REVOKE, REPORTS.VIEW)
  // tetiklendiğinde bu 5'li ağaç komple otomatik işaretlenir.
  // ────────────────────────────────────────────────────────────────
  "USERS.VIEW": [
    "AUDIT_LOGS.VIEW",
    "COMPANIES.VIEW",
    "DEPARTMENTS.VIEW",
    "EXAMS.VIEW",
    "ROLES.VIEW",
  ],
  "USERS.CREATE": [
    "USERS.VIEW",
    "AUDIT_LOGS.VIEW",
    "COMPANIES.VIEW",
    "DEPARTMENTS.VIEW",
    "ROLES.VIEW",
  ],
  "USERS.UPDATE": [
    "USERS.VIEW",
    "AUDIT_LOGS.VIEW",
    "COMPANIES.VIEW",
    "DEPARTMENTS.VIEW",
    "ROLES.VIEW",
  ],
  // Şifre değiştirme aksiyonu Personel listesinden tetiklenir —
  // kullanıcı bulmak için liste gerekir.
  "USERS.UPDATE_PASSWORD": ["USERS.VIEW"],
  // Delete, yalnızca liste ekranındaki confirm dialog'unu kullandığı için
  // ek bir widget/form bağımlılığı yoktur.
  "USERS.DELETE": ["USERS.VIEW"],
};

/**
 * Short human-readable reasons for each (owner → dependency) pair.
 * Used by the dependency popover in the role editor to tell the admin
 * WHY a permission was auto-checked. Turkish, UI-safe length (~1 line).
 * Only edges with a non-obvious reason are listed; for the generic
 * "X.{CREATE|UPDATE|DELETE} → X.VIEW" rule the UI shows a default label.
 */
/**
 * I18n key paths for each (owner → dep) pair. Lookups are done lazily through
 * `getDependencyReason()` so locale changes are picked up without rebuild.
 */
const REASON_KEY_PATHS: Record<string, Record<string, string>> = {
  "ANNOUNCEMENTS.VIEW": { "DEPARTMENTS.VIEW": "announcementsViewDept" },
  "ANNOUNCEMENTS.CREATE": { "DEPARTMENTS.VIEW": "announcementsCreateDept" },
  "ANNOUNCEMENTS.UPDATE": { "DEPARTMENTS.VIEW": "announcementsUpdateDept" },
  "EDUCATIONS.VIEW": {
    "EXAMS.VIEW": "educationsViewExam",
    "SURVEYS.VIEW": "educationsViewSurvey",
  },
  "EDUCATIONS.CREATE": {
    "EXAMS.VIEW": "educationsCreateExam",
    "SURVEYS.VIEW": "educationsCreateSurvey",
    "USERS.VIEW": "educationsCreateUsers",
  },
  "EDUCATIONS.UPDATE": {
    "EXAMS.VIEW": "educationsUpdateExam",
    "SURVEYS.VIEW": "educationsUpdateSurvey",
    "USERS.VIEW": "educationsUpdateUsers",
  },
  "INTERNS.VIEW": { "DEPARTMENTS.VIEW": "internsViewDept" },
  "INTERNS.CREATE": {
    "DEPARTMENTS.VIEW": "internsCreateDept",
    "USERS.VIEW": "internsCreateUsers",
  },
  "INTERNS.UPDATE": {
    "DEPARTMENTS.VIEW": "internsUpdateDept",
    "USERS.VIEW": "internsUpdateUsers",
  },
  "ROLES.CREATE": { "PERMISSIONS.VIEW": "rolesCreatePermissions" },
  "ROLES.UPDATE": { "PERMISSIONS.VIEW": "rolesUpdatePermissions" },
  "PERMISSIONS.ASSIGN": {
    "PERMISSIONS.VIEW": "permissionsAssignList",
    "USERS.VIEW": "permissionsAssignUsers",
  },
  "PERMISSIONS.REVOKE": {
    "PERMISSIONS.VIEW": "permissionsRevokeList",
    "USERS.VIEW": "permissionsRevokeUsers",
  },
  "REPORTS.VIEW": {
    "EDUCATIONS.VIEW": "reportsViewEducations",
    "USERS.VIEW": "reportsViewUsers",
  },
  "USERS.VIEW": {
    "AUDIT_LOGS.VIEW": "usersViewAuditLogs",
    "COMPANIES.VIEW": "usersViewCompanies",
    "DEPARTMENTS.VIEW": "usersViewDepartments",
    "EXAMS.VIEW": "usersViewExams",
    "ROLES.VIEW": "usersViewRoles",
  },
  "USERS.CREATE": {
    "AUDIT_LOGS.VIEW": "usersCreateAuditLogs",
    "COMPANIES.VIEW": "usersCreateCompanies",
    "DEPARTMENTS.VIEW": "usersCreateDepartments",
    "ROLES.VIEW": "usersCreateRoles",
  },
  "USERS.UPDATE": {
    "AUDIT_LOGS.VIEW": "usersUpdateAuditLogs",
    "COMPANIES.VIEW": "usersUpdateCompanies",
    "DEPARTMENTS.VIEW": "usersUpdateDepartments",
    "ROLES.VIEW": "usersUpdateRoles",
  },
};

export const PERMISSION_DEPENDENCY_REASONS: Record<
  string,
  Record<string, string>
> = REASON_KEY_PATHS;

/**
 * Generic fallback reason for a (owner → dep) pair that isn't listed in
 * PERMISSION_DEPENDENCY_REASONS. Covers the common "X.{CREATE|UPDATE|DELETE}
 * → X.VIEW" rule in one place.
 */
export function getDependencyReason(owner: string, dep: string): string {
  const explicitKey = REASON_KEY_PATHS[owner]?.[dep];
  if (explicitKey) {
    return i18n.t(`permissionDependencies.reasons.${explicitKey}`);
  }

  const [ownerModule, ownerAction] = owner.split(".");
  const [depModule, depAction] = dep.split(".");

  if (
    ownerModule === depModule &&
    depAction === "VIEW" &&
    ownerAction !== "VIEW"
  ) {
    return i18n.t("permissionDependencies.reasons.sameModuleViewRequired");
  }

  return i18n.t("permissionDependencies.reasons.genericDataDependency");
}

/** MODULE.ACTION → MODULE.PAGE  (implicit dependency for every module). */
function modulePageOf(key: string): string | null {
  const dot = key.indexOf(".");
  if (dot <= 0) return null;
  const module = key.slice(0, dot);
  const action = key.slice(dot + 1);
  if (action === "PAGE") return null;
  // Backend'de DASHBOARD.PAGE yok; DASHBOARD.EDUCATION_MAP_VIEW tekil aksiyon izni.
  // Auto PAGE kuralı burada geçerli olursa role update payload'ına geçersiz
  // DASHBOARD.PAGE eklenir ve backend 400 döner.
  if (module === "DASHBOARD") return null;
  return `${module}.PAGE`;
}

/**
 * Resolve all transitive dependencies for a permission key (BFS).
 * Returns the input key plus every required dependency, without duplicates.
 *
 * Implicit rule: every resolved key also pulls its MODULE.PAGE counterpart —
 * PAGE is the master switch for the module so ticking any action in that
 * module must auto-tick PAGE. Encoded here (not in the data map) to keep
 * the map shorter and enforce the rule uniformly.
 */
export function resolvePermissionWithDependencies(key: string): string[] {
  const result = new Set<string>();
  const queue: string[] = [key];
  while (queue.length > 0) {
    const current = queue.shift() as string;
    if (result.has(current)) continue;
    result.add(current);
    const deps = PERMISSION_DEPENDENCIES[current];
    if (deps) queue.push(...deps);
    const page = modulePageOf(current);
    if (page) queue.push(page);
  }
  return Array.from(result);
}

/**
 * For a given permission, return every other permission that would
 * auto-check it (direct callers only). Precomputed reverse map for the
 * dependency popover's "Bu yetki hangi yetkiler tarafından isteniyor?"
 * section.
 */
export function getReverseDependencies(key: string): string[] {
  const callers: string[] = [];
  for (const [owner, deps] of Object.entries(PERMISSION_DEPENDENCIES)) {
    if (deps.includes(key)) callers.push(owner);
  }
  return callers.sort();
}
